import { has } from "shared/utils/has";
import { Template } from "types/landlord/verificationTemplate";
import {
  AnimalScreeningReportVendors,
  ApplicantType,
  PetRestrictionType,
  VerificationNames
} from "shared/types";
import { tabTitle } from "./utils";
import { isValidUUID, isValidUrl } from "./helpers";
import { MAX_FEE_CENTS } from "./slices/AdditionalFeesSlice/constants";
import {
  MAX_ROUTES,
  MIN_ROUTES
} from "./slices/GeneralSettingView/templateRoutingConstants";
import {
  detectDuplicateRestrictionType,
  petPolicyToRestrictions
} from "shared/utils";

const onSaveValidation = {
  rules: (template: Template) => {
    if (template.recommendation_method === "rule_based") {
      if (
        template.auto_approve_delay_enabled &&
        (template.auto_approve_delay_value || 0) <= 0
      )
        return "Auto approve delay value must be greater than 0";

      const hasEmptyRuleSets = template.rule_criteria?.some(
        (criteria) => !criteria.rules.length
      );

      if (hasEmptyRuleSets) return "Empty rule sets are not allowed";

      const hasDeclineRule = template.rule_criteria?.some(
        (criteria) => criteria.type === "decline" && criteria.rules.length
      );
      const hasApproveRule = template.rule_criteria?.some(
        (criteria) => criteria.type === "approve" && criteria.rules.length
      );

      if (!hasDeclineRule || !hasApproveRule || !template.rule_criteria?.length)
        return "Template must have at least one approve and decline rule when using rule based recommendation";

      // Check if all rules have values
      const hasEmptyRule = template.rule_criteria.some((criteria) =>
        criteria.rules.some((rule) => !has(rule.value) || rule.value === "")
      );

      if (hasEmptyRule) return "All rules must have a value";
    }
  },
  criteria: (
    template: Template,
    setApplicantSettingsErrors: (errors: Record<string, string>) => void
  ) => {
    if (template.recommendation_method === "score_based") {
      const newErrors = {};

      Object.entries(
        template.screening_criteria?.applicant_settings || {}
      ).forEach(([key, entry]) => {
        if (entry.value !== 0 && !entry.value)
          newErrors[key] = "This value is required";
      });

      setApplicantSettingsErrors(newErrors);

      if (Object.keys(newErrors).length)
        return "All criteria must have a value";
    }
  },
  combinedIncomeVerificationMethods: (template: Template) => {
    for (const partnerType of ["applicant", "co_signer", "occupant"]) {
      const combinedIncomeSchema =
        template.verification_schemas[partnerType]?.[
          VerificationNames.CombinedIncome
        ];
      if (!combinedIncomeSchema) continue;

      // Income types that have verification methods
      for (const incomeType of ["employment", "self_employment", "other"]) {
        const config = combinedIncomeSchema[incomeType];
        if (!config) continue;

        // Ensure at least one verification method is enabled
        if (
          [
            "bank_allowed",
            "payroll_allowed",
            "paystub",
            "bank_statement",
            "w2"
          ].every((verificationMethodKey) => verificationMethodKey in config)
        ) {
          const isDocumentVerificationEnabled =
            config.paystub?.allowed ||
            config.bank_statement?.allowed ||
            config.w2?.allowed ||
            config.form_1099_nec?.allowed ||
            config.form_1099_misc?.allowed;
          const isAnyVerificationMethodEnabled =
            config.bank_allowed ||
            config.payroll_allowed ||
            isDocumentVerificationEnabled;

          if (!isAnyVerificationMethodEnabled)
            return `you need to select at least one verification method for the ${tabTitle[partnerType]} -> Income history ->  ${incomeType.replace("_", " ")}`;
        }
      }
    }

    return null;
  },
  petScreening: (template: Template) => {
    for (const partnerType of ["applicant", "co_signer", "occupant"]) {
      const petScreeningSchema =
        template.verification_schemas[partnerType]?.[
          VerificationNames.PetScreeningReport
        ];

      if (!petScreeningSchema?.settings?.pet_screening_enabled) continue;

      const { vendor, settings } = petScreeningSchema;
      const { screening_url, property_management_id } = settings || {};

      if (!vendor)
        return `Please select a vendor for ${tabTitle[partnerType]} pet screening`;

      if (!screening_url)
        return `Please enter a screening URL for ${tabTitle[partnerType]} pet screening`;

      if (!isValidUrl(screening_url))
        return `Please enter a valid screening URL for ${tabTitle[partnerType]} pet screening`;

      if (
        vendor === AnimalScreeningReportVendors.OurPetPolicy &&
        !property_management_id
      )
        return `Please enter a property manager ID for ${tabTitle[partnerType]} pet screening (required for OurPetPolicy)`;

      if (
        vendor === AnimalScreeningReportVendors.OurPetPolicy &&
        property_management_id &&
        !isValidUUID(property_management_id)
      )
        return `Please enter a valid property manager ID (UUID) for ${tabTitle[partnerType]} pet screening`;
    }

    return null;
  },
  templateRouting: (template: Template) => {
    // null/undefined means routing is off; "" means it is on but not filled in yet.
    if (template.routing_question == null) return null;
    if (!template.routing_question.trim())
      return "Enter a question for applicants";

    const labels = (template.routes ?? []).map((route) =>
      route.label.trim().toLowerCase()
    );
    if (labels.length < MIN_ROUTES || labels.length > MAX_ROUTES)
      return `Add between ${MIN_ROUTES} and ${MAX_ROUTES} answers`;
    if (labels.some((label) => !label)) return "Every answer needs a label";
    if (new Set(labels).size !== labels.length)
      return "Each answer needs a different label";

    return null;
  },
  additionalFees: (template: Template) => {
    const fees = template.template_fees || [];
    const enabledFees = fees.filter((f) => f.enabled);

    const hasEmptyFee = enabledFees.some((fee) => {
      return Number(fee.amount.cents) <= 0;
    });
    if (hasEmptyFee) return "All enabled additional fees must have a value";

    const hasFeeOverMaximum = enabledFees.some((fee) => {
      return fee.amount.cents > MAX_FEE_CENTS;
    });
    if (hasFeeOverMaximum)
      return `Additional fees cannot be greater than $${(MAX_FEE_CENTS / 100).toLocaleString()}`;
  },
  petPolicy: (template: Template) => {
    if (!template.pet_policy?.enabled) return null;

    if (!template.pet_policy?.restrictions)
      template.pet_policy.restrictions = petPolicyToRestrictions(
        template.pet_policy
      );

    if (!template.pet_policy.restrictions.length)
      return "At least one restriction is required";

    // Custom fields use "title" for title, and "value" for description;
    // Title is required, but description is optional.
    if (
      template.pet_policy.restrictions.some(
        (restriction) =>
          restriction.type === PetRestrictionType.CustomField &&
          !restriction.title?.trim()
      )
    )
      return "A custom field is missing a title";

    if (
      template.pet_policy.restrictions.some(
        (restriction) =>
          restriction.type !== PetRestrictionType.CustomField &&
          (restriction.value === "" || !has(restriction.value))
      )
    )
      return "A restriction is missing a value";

    const duplicateType = detectDuplicateRestrictionType(
      template.pet_policy.restrictions
    );
    if (duplicateType)
      return `There are conflicting "${duplicateType}" restrictions`;

    delete template.pet_policy?.restrictions;
  },
  boomFinance: (template: Template) => {
    for (const partnerType of [
      ApplicantType.Applicant,
      ApplicantType.Occupant
    ]) {
      const boomFinanceSettings =
        template.verification_schemas[partnerType]?.[
          VerificationNames.BoomFinanceVerification
        ];
      if (!boomFinanceSettings?.boom_finance_verification_enabled) continue;

      if (
        typeof boomFinanceSettings?.lender_selection_step_enabled !== "boolean"
      )
        return `Please select a lender selection mode for ${partnerType}s`;

      if (!boomFinanceSettings?.available_lender_ids?.length)
        return `Please select at least one lender for ${partnerType}s`;
    }

    return null;
  }
};

interface TemplateValidationOptions {
  setApplicantSettingsErrors?: (errors: Record<string, string>) => void;
  onError?: (mesage: string, type: "error" | "warning") => void;
}

interface TemplateValidationResult {
  isValid: boolean;
  message?: string;
  errorType?: "error" | "warning";
}

/**
 * Validates a template before save (for both create and update)
 * @param template - The template to validate
 * @param options - Optional callbacks for error handling
 * @returns Validation result with isValid flag and optional error message
 */
export const getTemplateRoutingError = (template: Template) =>
  onSaveValidation.templateRouting(template);

export const validateTemplate = (
  template: Template,
  options?: TemplateValidationOptions
): TemplateValidationResult => {
  const { setApplicantSettingsErrors, onError } = options || {};

  // Validate rules
  const rulesError = onSaveValidation.rules(template);
  if (rulesError) {
    const message = `Error in rules: ${rulesError}`;
    onError?.(message, "error");
    return { isValid: false, message, errorType: "error" };
  }

  // Validate criteria
  const criteriaError = onSaveValidation.criteria(
    template,
    setApplicantSettingsErrors || (() => {})
  );
  if (criteriaError) {
    const message = `Error in criteria: ${criteriaError}`;
    onError?.(message, "error");
    return { isValid: false, message, errorType: "error" };
  }

  // Validate combined income verification methods
  const combinedIncomeVerificationMethodsError =
    onSaveValidation.combinedIncomeVerificationMethods(template);
  if (combinedIncomeVerificationMethodsError) {
    const message = `Error in combined income verification methods: ${combinedIncomeVerificationMethodsError}`;
    onError?.(message, "warning");
    return { isValid: false, message, errorType: "warning" };
  }

  // Validate pet screening
  const petScreeningError = onSaveValidation.petScreening(template);
  if (petScreeningError) {
    const message = petScreeningError;
    onError?.(message, "error");
    return { isValid: false, message, errorType: "error" };
  }

  const additionalFeesError = onSaveValidation.additionalFees(template);
  if (additionalFeesError) {
    const message = `Error in additional fees: ${additionalFeesError}`;
    onError?.(message, "error");
    return { isValid: false, message, errorType: "error" };
  }

  const templateRoutingError = onSaveValidation.templateRouting(template);
  if (templateRoutingError) {
    onError?.(templateRoutingError, "error");
    return {
      isValid: false,
      message: templateRoutingError,
      errorType: "error"
    };
  }

  const petPolicyError = onSaveValidation.petPolicy(template);
  if (petPolicyError) {
    const message = petPolicyError + " in the pet policy settings";
    onError?.(message, "error");
    return { isValid: false, message, errorType: "error" };
  }

  const boomFinanceError = onSaveValidation.boomFinance(template);
  if (boomFinanceError) {
    const message = boomFinanceError + " in the Boom Finance settings";
    onError?.(message, "error");
    return { isValid: false, message, errorType: "error" };
  }

  return { isValid: true };
};
